import "./firebase.ts";
import { requestOtp, submitOtp, watchAuth } from "./auth.ts";
import {
  watchActiveLaunch,
  watchLaunch,
  type Launch,
  type PickedWebinar,
} from "./launch.ts";
import { fetchSignedUrl, mountStream } from "./stream.ts";
import {
  renderShell,
  renderLoading,
  renderError,
  renderEmpty,
  renderSignIn,
  renderCountdown,
  renderPlayerShell,
} from "./ui.ts";

const root = document.getElementById("app");
if (!root) throw new Error("No #app element");

let cleanupListener: (() => void) | null = null;
let cleanupCountdown: (() => void) | null = null;
let cleanupPlayer: (() => void) | null = null;

function teardownView(): void {
  cleanupCountdown?.();
  cleanupCountdown = null;
  cleanupPlayer?.();
  cleanupPlayer = null;
}

function teardownAll(): void {
  cleanupListener?.();
  cleanupListener = null;
  teardownView();
}

function parseRoute(): { kind: "root" } | { kind: "launch"; launchId: string } {
  const path = window.location.pathname.replace(/^\/+|\/+$/g, "");
  if (!path) return { kind: "root" };
  return { kind: "launch", launchId: path };
}

async function renderPicked(
  wrap: HTMLElement,
  launch: Launch,
  picked: PickedWebinar
): Promise<void> {
  switch (picked.kind) {
    case "live":
    case "vod": {
      const host = renderPlayerShell(wrap, picked.webinar.title || launch.title);
      try {
        const signed = await fetchSignedUrl(picked.videoId);
        cleanupPlayer = await mountStream(host, signed.hlsManifestURL, {
          isLive: picked.kind === "live",
          poster: signed.thumbnailURL,
        });
      } catch (err) {
        renderError(
          wrap,
          err instanceof Error ? err.message : "Kon de stream niet laden."
        );
      }
      return;
    }
    case "upcoming":
      cleanupCountdown = renderCountdown(
        wrap,
        picked.webinar.title || launch.title,
        picked.scheduledAt
      );
      return;
    case "processing":
      renderEmpty(
        wrap,
        "De opname wordt verwerkt. Probeer het over een paar minuten opnieuw."
      );
      return;
    case "none":
      renderEmpty(wrap, "Er staat momenteel geen sessie klaar.");
      return;
  }
}

function renderRoot(wrap: HTMLElement): void {
  renderLoading(wrap, "Sessie zoeken…");
  cleanupListener = watchActiveLaunch(async (snap) => {
    teardownView();
    if (snap.kind === "loading") {
      renderLoading(wrap, "Sessie zoeken…");
      return;
    }
    if (snap.kind === "none") {
      renderEmpty(wrap, "Er is momenteel geen actieve sessie.");
      return;
    }
    await renderPicked(wrap, snap.launch, snap.picked);
  });
}

function renderLaunchView(wrap: HTMLElement, launchId: string): void {
  renderLoading(wrap, "Sessie laden…");
  cleanupListener = watchLaunch(launchId, async (snap) => {
    teardownView();
    if (snap.kind === "loading") {
      renderLoading(wrap, "Sessie laden…");
      return;
    }
    if (snap.kind === "not_found") {
      renderError(wrap, "Sessie niet gevonden of niet toegankelijk.");
      return;
    }
    await renderPicked(wrap, snap.launch, snap.picked);
  });
}

function route(): void {
  teardownAll();
  const wrap = renderShell(root!);
  const r = parseRoute();
  if (r.kind === "root") {
    renderRoot(wrap);
    return;
  }
  renderLaunchView(wrap, r.launchId);
}

watchAuth((user) => {
  teardownAll();
  const wrap = renderShell(root!);
  if (!user) {
    renderSignIn(wrap, {
      onRequestOtp: requestOtp,
      onSubmitOtp: async (email, code) => {
        await submitOtp(email, code);
      },
    });
    return;
  }
  route();
});

window.addEventListener("popstate", route);
